/**
 * Per-race results scene — 1st/2nd/3rd horses + scorers.
 *
 * Polish pass: a giant PHOTO FINISH header (Bebas Neue + theme glow), three
 * podium cards re-ordered to put the winner in the center with a tall
 * jockey-helmet badge, scale animations on entrance, and a scorers panel
 * with row-stagger animation that styles the winners' names with the
 * celebration color.
 */

import type { RaceResults } from '../state/types.js';

export interface ResultsHandle {
  el: HTMLDivElement;
  setResults(r: RaceResults): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes hr-results-card-in {
    from { opacity: 0; transform: translateY(28px) scale(0.94); }
    to   { opacity: 1; transform: translateY(0)    scale(var(--card-scale, 1)); }
  }
  @keyframes hr-results-row-in {
    from { opacity: 0; transform: translateX(-16px); }
    to   { opacity: 1; transform: translateX(0);     }
  }
  @keyframes hr-results-glow {
    0%, 100% { box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 60px var(--card-silk); }
    50%      { box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 100px var(--card-silk); }
  }
  .hr-results {
    position: absolute; inset: 0;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 56px;
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    box-sizing: border-box;
    z-index: 2;
  }
  .hr-results-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 140px; letter-spacing: 0.1em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 8px 0 var(--sg-secondary, #3B0764), 0 0 80px rgba(255,196,61,0.45);
    line-height: 0.95;
    margin-bottom: 8px;
  }
  .hr-results-sub {
    font-family: 'Oswald', sans-serif;
    font-size: 26px; letter-spacing: 0.32em;
    color: var(--sg-accent, #22C55E);
    text-transform: uppercase;
    margin-bottom: 36px;
    text-shadow: 0 0 20px rgba(34,197,94,0.4);
  }
  .hr-results-podium {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 28px; min-width: 1200px;
    align-items: end;
    justify-items: center;
  }
  .hr-results-card {
    display: flex; flex-direction: column; align-items: center; gap: 14px;
    padding: 28px 28px 22px 28px;
    background: linear-gradient(180deg, rgba(15,23,42,0.92) 0%, rgba(3,6,23,0.98) 100%);
    border-radius: 20px;
    border: 4px solid var(--card-silk);
    box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 60px var(--card-silk);
    width: 100%; max-width: 360px;
    --card-scale: 1;
    animation: hr-results-card-in 460ms cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  }
  .hr-results-card[data-place="1"] {
    --card-scale: 1.12;
    animation-delay: 320ms,  0s;
    animation: hr-results-card-in 520ms cubic-bezier(0.34, 1.56, 0.64, 1) 280ms backwards,
               hr-results-glow 2.4s ease-in-out 800ms infinite;
    z-index: 2;
    padding: 36px 28px 28px 28px;
  }
  .hr-results-card[data-place="2"] { animation-delay: 80ms; }
  .hr-results-card[data-place="3"] { animation-delay: 180ms; }
  .hr-results-place {
    font-family: 'Bebas Neue', sans-serif;
    color: var(--sg-primary, #FFC43D);
    line-height: 0.95;
    text-shadow: 0 4px 0 rgba(0,0,0,0.6);
    letter-spacing: 0.05em;
  }
  .hr-results-card[data-place="1"] .hr-results-place { font-size: 110px; }
  .hr-results-card[data-place="2"] .hr-results-place,
  .hr-results-card[data-place="3"] .hr-results-place { font-size: 78px; }
  .hr-results-badge {
    background: var(--card-silk);
    color: #000;
    font-family: 'Bebas Neue', sans-serif;
    font-weight: 700;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    box-shadow: inset 0 -10px 0 rgba(0,0,0,0.18), 0 6px 0 rgba(0,0,0,0.4);
    text-shadow: 0 2px 0 rgba(255,255,255,0.25);
  }
  .hr-results-card[data-place="1"] .hr-results-badge {
    width: 116px; height: 116px; font-size: 76px;
  }
  .hr-results-card[data-place="2"] .hr-results-badge,
  .hr-results-card[data-place="3"] .hr-results-badge {
    width: 88px; height: 88px; font-size: 56px;
  }
  .hr-results-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    text-align: center; line-height: 1.05;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .hr-results-card[data-place="1"] .hr-results-name { font-size: 38px; }
  .hr-results-card[data-place="2"] .hr-results-name,
  .hr-results-card[data-place="3"] .hr-results-name { font-size: 30px; }
  .hr-results-scorers {
    margin-top: 36px; min-width: 980px; max-width: 1100px;
    background: linear-gradient(180deg, rgba(6,12,30,0.85), rgba(3,6,18,0.95));
    border-radius: 18px;
    padding: 22px 32px;
    border: 2px solid var(--sg-primary, #FFC43D);
    box-shadow: 0 12px 36px rgba(0,0,0,0.55);
  }
  .hr-results-scorers-h {
    font-family: 'Oswald', sans-serif;
    font-size: 26px; font-weight: 700;
    letter-spacing: 0.22em;
    color: var(--sg-primary, #FFC43D);
    margin-bottom: 14px;
    text-align: center;
    text-transform: uppercase;
  }
  .hr-results-scorers-list {
    display: grid; grid-template-columns: repeat(2, 1fr);
    gap: 8px 24px;
  }
  .hr-results-scorer-row {
    display: flex; justify-content: space-between; align-items: baseline;
    font-size: 22px;
    padding: 8px 12px;
    border-radius: 10px;
    background: rgba(255,255,255,0.04);
    animation: hr-results-row-in 320ms ease-out backwards;
  }
  .hr-results-scorer-row:nth-child(1) { animation-delay: 480ms; }
  .hr-results-scorer-row:nth-child(2) { animation-delay: 540ms; }
  .hr-results-scorer-row:nth-child(3) { animation-delay: 600ms; }
  .hr-results-scorer-row:nth-child(4) { animation-delay: 660ms; }
  .hr-results-scorer-row:nth-child(5) { animation-delay: 720ms; }
  .hr-results-scorer-row:nth-child(6) { animation-delay: 780ms; }
  .hr-results-scorer-row:nth-child(7) { animation-delay: 840ms; }
  .hr-results-scorer-row:nth-child(8) { animation-delay: 900ms; }
  .hr-results-scorer-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    color: #fff;
  }
  .hr-results-scorer-pick {
    font-size: 0.85em; opacity: 0.6;
    margin-left: 8px;
  }
  .hr-results-scorer-points {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    color: var(--sg-celebration, #22C55E);
    text-shadow: 0 0 12px rgba(34,197,94,0.5);
  }
  .hr-results-scorers-empty {
    text-align: center; opacity: 0.7;
    font-style: italic; font-size: 22px;
    padding: 8px 0;
  }
  .hr-results-payout {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    font-size: 26px;
    color: var(--sg-celebration, #22C55E);
    text-shadow: 0 0 14px rgba(34,197,94,0.45);
    letter-spacing: 0.06em;
  }
  .hr-results-fullboard {
    grid-column: 1 / -1;
    margin-top: 24px;
    display: grid; grid-template-columns: repeat(5, 1fr);
    gap: 12px;
  }
  .hr-results-fullboard-cell {
    display: flex; flex-direction: column; align-items: center;
    background: rgba(15,23,42,0.72);
    border-radius: 10px;
    padding: 10px 8px;
    border: 2px solid var(--cell-silk);
    gap: 4px;
  }
  .hr-results-fullboard-place {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 28px;
    color: #fff;
    letter-spacing: 0.04em;
  }
  .hr-results-fullboard-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #cbd5e1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: center;
  }
  .hr-results-fullboard-payout {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    font-size: 18px;
    color: var(--sg-primary, #FFC43D);
  }
`;

function ensureStyles(): void {
  if (document.getElementById('hr-results-style')) return;
  const s = document.createElement('style');
  s.id = 'hr-results-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function ordinal(n: number): string {
  if (n === 1) return '1ST';
  if (n === 2) return '2ND';
  if (n === 3) return '3RD';
  return `${n}TH`;
}

export function createResults(stage: HTMLElement): ResultsHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'hr-results';
  el.setAttribute('data-scene', 'results');
  el.setAttribute('data-testid', 'hr-results');

  const title = document.createElement('div');
  title.className = 'hr-results-title';
  title.textContent = 'PHOTO FINISH';
  const sub = document.createElement('div');
  sub.className = 'hr-results-sub';
  sub.textContent = 'TOP 3';

  const podium = document.createElement('div');
  podium.className = 'hr-results-podium';
  podium.setAttribute('data-testid', 'hr-results-podium');

  const scorersSection = document.createElement('div');
  scorersSection.className = 'hr-results-scorers';
  scorersSection.setAttribute('data-testid', 'hr-results-scorers');

  el.appendChild(title);
  el.appendChild(sub);
  el.appendChild(podium);
  el.appendChild(scorersSection);
  stage.appendChild(el);

  return {
    el,
    setResults(r) {
      podium.innerHTML = '';
      const top3 = r.placings.filter((p) => p.place <= 3).sort((a, b) => a.place - b.place);
      // Re-order: 2nd, 1st, 3rd so the winner sits center.
      const ordered = [top3.find((p) => p.place === 2), top3.find((p) => p.place === 1), top3.find((p) => p.place === 3)];
      for (const p of ordered) {
        if (!p) continue;
        const card = document.createElement('div');
        card.className = 'hr-results-card';
        card.setAttribute('data-place', String(p.place));
        card.style.setProperty('--card-silk', p.silkColor);
        const place = document.createElement('div');
        place.className = 'hr-results-place';
        place.textContent = ordinal(p.place);
        const badge = document.createElement('div');
        badge.className = 'hr-results-badge';
        badge.textContent = String(p.number);
        const name = document.createElement('div');
        name.className = 'hr-results-name';
        name.textContent = p.name;
        const payout = document.createElement('div');
        payout.className = 'hr-results-payout';
        payout.textContent = `+${p.payout} pts`;
        card.appendChild(place);
        card.appendChild(badge);
        card.appendChild(name);
        card.appendChild(payout);
        podium.appendChild(card);
      }

      // Compact full-board strip (places 4..8) so viewers see all placings + payouts.
      renderFullBoard(podium, r.placings);

      scorersSection.innerHTML = '';
      const scoringHeader = document.createElement('div');
      scoringHeader.className = 'hr-results-scorers-h';
      scoringHeader.textContent =
        r.scorers.length > 0
          ? `WINNERS — +${r.totalPointsAwarded} POINTS AWARDED`
          : 'NO WINNING BETS THIS RACE';
      scorersSection.appendChild(scoringHeader);
      const list = document.createElement('div');
      list.className = 'hr-results-scorers-list';
      const top = r.scorers.slice(0, 8);
      if (top.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'hr-results-scorers-empty';
        empty.textContent = 'Better luck next race!';
        scorersSection.appendChild(empty);
      } else {
        for (const s of top) {
          const row = document.createElement('div');
          row.className = 'hr-results-scorer-row';
          const left = document.createElement('span');
          left.className = 'hr-results-scorer-name';
          left.innerHTML = `${escape(s.display_name)}<span class="hr-results-scorer-pick">picked #${s.horse_number}</span>`;
          const right = document.createElement('span');
          right.className = 'hr-results-scorer-points';
          right.textContent = `+${s.points}`;
          row.appendChild(left);
          row.appendChild(right);
          list.appendChild(row);
        }
        scorersSection.appendChild(list);
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
  };
}

function escape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Render places 4..8 as compact cells under the top-3 podium. */
function renderFullBoard(
  podium: HTMLDivElement,
  placings: Array<{ place: number; number: number; name: string; silkColor: string; payout: number }>,
): void {
  const tail = placings.filter((p) => p.place >= 4).sort((a, b) => a.place - b.place);
  if (tail.length === 0) return;
  const strip = document.createElement('div');
  strip.className = 'hr-results-fullboard';
  for (const p of tail) {
    const cell = document.createElement('div');
    cell.className = 'hr-results-fullboard-cell';
    cell.style.setProperty('--cell-silk', p.silkColor);
    cell.innerHTML = `
      <div class="hr-results-fullboard-place">${p.place}TH</div>
      <div class="hr-results-fullboard-name">#${p.number} ${escape(p.name)}</div>
      <div class="hr-results-fullboard-payout">+${p.payout} pts</div>
    `;
    strip.appendChild(cell);
  }
  podium.appendChild(strip);
}
